import React, {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import CardExpansion from '@ifca-root/react-component/src/components/CardList/CardExpansion'
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { RejectDialog } from 'components/Dialog/RejectDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  AcctPermission,
  ApprovalStatus,
  useActionOnChangeApportionmentStatusMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { formatDate } from 'helpers/StringNumberFunction/FormatDate'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ChangeApportionmentDetailContent } from '../ChangeApportionmentDetail/ChangeApportionmentDetailContent'
import { usePermissionChecker } from 'helpers/Hooks/usePermissionChecker'

interface PostingListProps {
  filteredList: any[]
  listLoading: boolean
  listStatus: string
  userList: any[]
  fetchMore: Function
  setOriginalListing: Function
  postIDs: Set<string>
  setPostIDs: Function
  CompanyID: string
}

export const ChangeApportionmentPostingList: FC<PostingListProps> = ({
  filteredList,
  listLoading,
  listStatus,
  userList,
  fetchMore,
  CompanyID,
  postIDs,
  setPostIDs,
  setOriginalListing,
}) => {
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)
  const [selectAll, setSelectAll] = useState(false)
  const getSearch = JSON.parse(localStorage.getItem('searchFilter'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [changeStatus, setChangeStatus] = useState('')
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [clickReject, setIsReject] = useState(false)
  const [rejectDialogInput, setRejectDialogInput] = useState({
    RejectionRemark: '',
  })
  const [errMessage, setErrMessage] = useState<string | null>(null)
  const [errDialog, setErrDialog] = useState(false)
  const [errApproveDialog, setErrApproveDialog] = useState(false)

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    approveRejectChange,
    { loading: approveRejectLoading },
  ] = useActionOnChangeApportionmentStatusMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrApproveDialog(true)
      setPostIDs(new Set([]))
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      if (changeStatus === ApprovalStatus.Rejected) {
        setSnackBarMsg(SystemMsgs.rejectNewRecord())
      } else {
        setSnackBarMsg(SystemMsgs.createNewRecord())
      }
      setOriginalListing([])
      setPostIDs(new Set([]))
      fetchMore({
        variables: {
          CompanyID: CompanyID,
          StatusArr: ['SUBMIT'],
          take: 30,
          skip: 0,
          searchValue:
            getSearch?.name === '' || getSearch?.name === undefined
              ? undefined
              : getSearch?.name,
        },
      })
    },
  })

  const sortedList = useMemo(() => {
    return filteredList.sort(
      (a, b) =>
        Object.values(ApprovalStatus).indexOf(a?.ApprovalStatus) -
        Object.values(ApprovalStatus).indexOf(b?.ApprovalStatus)
    )
  }, [filteredList])

  /* -------------------------------------------- */
  /*              CHECKBOX HANDLERS               */
  /* -------------------------------------------- */
  const handleCheckBoxChange = (
    e: ChangeEvent<HTMLInputElement>,
    changeID: string
  ) => {
    const temp = new Set(postIDs)

    if (e.target.checked) {
      temp.add(changeID)
    } else {
      temp.delete(changeID)
      setSelectAll(false)
    }

    setPostIDs(temp)
  }

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (listLoading) return

    const isChecked = e.target.checked
    setSelectAll(isChecked)

    const temp = new Set(
      isChecked ? checkedFilteredList.map(el => el.apportionment_id) : []
    )

    setPostIDs(temp)
  }

  const checkedFilteredList =
    filteredList && filteredList.length > 0 ? filteredList : []

  /* -------------------------------------------- */
  /*              VALIDATION & SUBMIT             */
  /* -------------------------------------------- */
  const validateSelection = (listChangeIDs: string[]) => {
    const validIDs = listChangeIDs.filter(id =>
      filteredList.some(item => item?.apportionment_id === id)
    )

    const invalidItems = validIDs.filter(id => {
      const item = filteredList.find(item => item?.apportionment_id === id)
      return item?.ApprovalStatus !== 'SUBMIT'
    })

    if (validIDs.length === 0) {
      setErrMessage('No valid items selected. Please refresh the page.')
      setErrDialog(true)
      return []
    }

    if (invalidItems.length > 0) {
      setErrMessage(
        'Some selected items are no longer available for approval. Please refresh the page.'
      )
      setErrDialog(true)
      return []
    }

    return validIDs
  }

  const onSubmit = (listChangeIDs: string[], status: ApprovalStatus) => {
    const validIDs = validateSelection(listChangeIDs)

    if (validIDs.length === 0) {
      setPostIDs(new Set([]))
      return
    }

    if (validIDs.length < listChangeIDs.length) {
      setPostIDs(new Set(validIDs))
      return
    }

    setChangeStatus(status)
    approveRejectChange({
      variables: {
        apportionment_ids: validIDs,
        ApprovalStatus: status,
        CompanyID: CompanyID,
        input:
          status === ApprovalStatus.Rejected
            ? {
                RejectionDate: new Date().toISOString(),
                RejectionRemark: rejectDialogInput?.RejectionRemark,
              }
            : undefined,
      },
    })
  }

  useEffect(() => {
    if (clickReject) {
      const listChangeIDs: string[] = Array.from(postIDs || [])
      onSubmit(listChangeIDs, ApprovalStatus.Rejected)
      setIsReject(false)
    }
  }, [clickReject])

  const listChangeIDs: string[] = Array.from(postIDs || [])

  const getTotalPercentageChange = (details: any[]) => {
    if (!details || details.length === 0) return 0
    return details.reduce((sum, d) => {
      const diff = Math.abs((d.new_percentage || 0) - (d.old_percentage || 0))
      return sum + diff
    }, 0)
  }
  const { handlePermDisabled } = usePermissionChecker()

  return (
    <>
      {listLoading && <Loading />}
      {approveRejectLoading && <Loading />}

      <ContentWrapper float overflow>
        {listStatus === 'SUBMIT' && checkedFilteredList?.length !== 0 ? (
          <span className="mdDesc flex-space" style={{ paddingLeft: '7px' }}>
            <Checkbox
              value=""
              color="primary"
              onChange={handleSelectAll}
              style={{ marginLeft: '-5px', marginTop: '-5px' }}
              checked={
                postIDs?.size > 0 && postIDs.size === checkedFilteredList.length
              }
              disabled={
                listLoading ||
                handlePermDisabled({
                  companyID: CompanyID,
                  permEnum:
                    AcctPermission.FixedAssetsApportionmentApproveReject,
                })
              }
            />
            <span style={{ marginLeft: '9px' }}>
              Select All{listLoading ? ' (Loading...)' : ''}
            </span>
          </span>
        ) : null}

        <List className="core-list">
          {checkedFilteredList === undefined ||
          checkedFilteredList?.length === 0 ? (
            <EmptyList
              title="No Record found"
              subtitle="Add a new record now."
            />
          ) : (
            <InfiniteScroll
              dataLength={checkedFilteredList?.length || 0}
              next={() => {
                const currentLength = filteredList?.length
                fetchMore({
                  variables: {
                    CompanyID: CompanyID,
                    StatusArr: [listStatus],
                    take: 30,
                    skip: currentLength,
                    searchValue:
                      getSearch?.name === '' || getSearch?.name === undefined
                        ? undefined
                        : getSearch?.name,
                  },
                })
              }}
              hasMore={true}
              style={{ padding: '8px 6px' }}
              loader={
                listLoading && (
                  <div style={{ textAlign: 'center' }}>
                    <CircularProgress />
                  </div>
                )
              }
              endMessage={
                <p style={{ textAlign: 'center' }}>
                  <b>-</b>
                </p>
              }
            >
              {checkedFilteredList.map((el, index) => {
                const totalChange = getTotalPercentageChange(el?.Details)
                return (
                  <CardExpansion
                    key={index}
                    backPanelColor="#f5f5f5"
                    summary={
                      <ListItem style={{ paddingBottom: '25px' }}>
                        {listStatus === 'SUBMIT' && (
                          <FormControl
                            component="fieldset"
                            style={{ width: '16px', paddingRight: '16px' }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={el.apportionment_id}
                                  onChange={e =>
                                    handleCheckBoxChange(e, el.apportionment_id)
                                  }
                                  onClick={e => e.stopPropagation()}
                                  checked={postIDs.has(el.apportionment_id)}
                                  color="primary"
                                  style={{
                                    marginLeft: '10px',
                                    marginRight: '-5px',
                                  }}
                                  disabled={
                                    listLoading ||
                                    handlePermDisabled({
                                      companyID: CompanyID,
                                      permEnum:
                                        AcctPermission.FixedAssetsApportionmentApproveReject,
                                    })
                                  }
                                />
                              }
                              label=""
                            />
                          </FormControl>
                        )}
                        <ListItemText
                          primary={
                            <>
                              <span className="desc date-width">
                                {`${formatDate(el?.change_date)}` || 'N/A'}
                              </span>
                              <span className="xsTitle flex-space">
                                {el?.DocNo}
                              </span>
                              <span className="xsTitle">{el?.Asset?.name}</span>
                            </>
                          }
                          secondary={
                            <>
                              <Grid container spacing={0}>
                                <Grid container className="table-content">
                                  <Grid item xs={6}>
                                    <span className="desc flex-space">
                                      {el?.Asset?.Category?.name}
                                    </span>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    style={{ textAlign: 'right' }}
                                  >
                                    <span className="desc">
                                      <span style={{ color: '#666' }}>
                                        {el?.Details?.length || 0} dept(s)
                                      </span>
                                      {' | '}
                                      <span style={{ color: 'orange' }}>
                                        {totalChange.toFixed(2)}% changed
                                      </span>
                                    </span>
                                  </Grid>
                                </Grid>

                                <Grid container>
                                  <Grid item xs={12}>
                                    <span className="desc">{el?.reason}</span>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </>
                          }
                        />
                      </ListItem>
                    }
                  >
                    <ChangeApportionmentDetailContent
                      listEl={el}
                      listStatus={'SUBMIT'}
                      userList={userList}
                      mode={'expansion'}
                    />
                  </CardExpansion>
                )
              })}
            </InfiniteScroll>
          )}
        </List>
      </ContentWrapper>

      {listStatus === 'SUBMIT' && (
        <AccountFooter
          options={[
            {
              name: 'Reject',
              onClick: () => {
                setOpenRejectDialog(true)
              },
              color: 'secondary',
              props: {
                disabled:
                  postIDs.size === 0 ||
                  handlePermDisabled({
                    companyID: CompanyID,
                    permEnum:
                      AcctPermission.FixedAssetsApportionmentApproveReject,
                  }),
              },
            },
            {
              name: 'Approve',
              onClick: () => {
                onSubmit(listChangeIDs, ApprovalStatus.Completed)
              },
              color: 'primary',
              props: {
                disabled:
                  postIDs.size === 0 ||
                  handlePermDisabled({
                    companyID: CompanyID,
                    permEnum:
                      AcctPermission.FixedAssetsApportionmentApproveReject,
                  }),
              },
            },
          ]}
        />
      )}

      <RejectDialog
        data={filteredList}
        openRejectDialog={openRejectDialog}
        setOpenRejectDialog={setOpenRejectDialog}
        setRejectDialogInput={setRejectDialogInput}
        setIsReject={setIsReject}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />

      <ErrorDialog
        errorDia={errApproveDialog}
        setErrorDia={setErrApproveDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Approval Error'}
        isBool
        onclick={() => {
          setErrApproveDialog(false)
          setOriginalListing([])
          setPostIDs(new Set([]))
          fetchMore({
            variables: {
              CompanyID: CompanyID,
              StatusArr: ['SUBMIT'],
              take: 30,
              skip: 0,
              searchValue:
                getSearch?.name === '' || getSearch?.name === undefined
                  ? undefined
                  : getSearch?.name,
            },
          })
        }}
      />
    </>
  )
}
